import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useMsal } from '@azure/msal-react';
import GraphApi from './api/graph/graph-api';
import UserApi from './api/user/user-api-factory';
import { MenuAPI } from './api/menu/mock-menu-api';
import { SignalRApi } from './api/signalR/signalr-api';
import { CacheService } from './api/cache-service';
import { arrayAny } from './utils/arrayUtils';

const SignalRContext = createContext();
const UserProfileContext = createContext();
const MenuContext = createContext();

// Map backend apiRoles (e.g. "tenant_Manager") to menu roles
// (e.g. "Tenant.Manager") used in mock-menu-api.js
const mapApiRoleToMenuRole = apiRole => {
  const roleMap = {
    tenant_Manager: 'Tenant.Manager',
    user_Basic: 'User.Basic',
    billing_Admin: 'Billing.Admin',
    application_Developer: 'Application.Developer',
  };
  return roleMap[apiRole] || apiRole;
};

const resolveAccountObjectId = account => (
  account?.idTokenClaims?.oid ||
  account?.idTokenClaims?.sub ||
  account?.localAccountId ||
  null
);

const loadUserLocale = async usr => {
  if (usr && usr.locale) {
    await import(`moment/locale/${usr.locale}`);
    moment.locale(usr.locale);
    return;
  }
  moment.locale('en-gb');
};

export const UserProfileProvider = ({ children }) => {
  const { instance, inProgress, accounts } = useMsal();
  const graphApi = useMemo(() => new GraphApi(instance, inProgress, accounts, false), [instance, inProgress, accounts]);
  const userApi = useMemo(() => new UserApi(instance, inProgress, accounts, false), [instance, inProgress, accounts]);
  const [fetchingProfileImage, setFetchingProfileImage] = useState(false);
  const [fetchProfileImageComplete, setFetchProfileImageComplete] = useState(false);
  const [fetchingUserData, setFetchingUserData] = useState(false);
  const [fetchUserDataComplete, setFetchUserDataComplete] = useState(false);
  const [me, setMe] = useState(null);
  const [myProfile, setMyProfile] = useState(null);
  const [myAAPhotoUrl, setMyAAPhotoUrl] = useState('');
  
  useEffect(() => {
    if (graphApi && userApi && !fetchingUserData && !fetchUserDataComplete) {
      const fetchUserDataAsync = async () => {
        setFetchingUserData(true);
        try {
          const activeAccount = instance.getActiveAccount() || accounts?.[0];
          const claimedBackendUserId = resolveAccountObjectId(activeAccount);
          const userCacheKey = claimedBackendUserId ? `backend_user_profile_${claimedBackendUserId}` : null;

          // Load cached backend profile immediately so role-based menus can render
          // without waiting for Graph.
          if (userCacheKey) {
            const cachedUser = CacheService.get(userCacheKey, Number.MAX_SAFE_INTEGER);
            if (cachedUser) {
              setMyProfile(cachedUser);
              await loadUserLocale(cachedUser);
            }
          }

          // Start Graph fetch in parallel; user profile no longer depends on it.
          const graphPromise = graphApi.getMeDetails()
            .then(m => {
              setMe(m);
              return m;
            })
            .catch(graphError => {
              console.error('Failed to load Graph user details.', graphError);
              return null;
            });

          let backendProfile = null;

          // Prefer account-claim object id for backend profile lookup.
          if (claimedBackendUserId) {
            try {
              backendProfile = await userApi.user(claimedBackendUserId);
            } catch (userError) {
              console.error('Failed to load backend profile from account claims.', userError);
            }
          }

          const graphMe = await graphPromise;
          const graphDerivedUserId = graphMe?.objectId || graphMe?.id;

          // Fallback: if claim-based lookup failed or ids differ, try graph-derived id.
          if (!backendProfile && graphDerivedUserId && graphDerivedUserId !== claimedBackendUserId) {
            try {
              backendProfile = await userApi.user(graphDerivedUserId);
            } catch (graphUserError) {
              console.error('Failed to load backend profile from Graph identifier.', graphUserError);
            }
          }

          if (backendProfile) {
            setMyProfile(backendProfile);
            await loadUserLocale(backendProfile);

            const profileId = claimedBackendUserId || graphDerivedUserId;
            if (profileId) {
              CacheService.set(`backend_user_profile_${profileId}`, backendProfile);
            }
          }
        } catch (error) {
          console.error('Failed to hydrate user context.', error);
        } finally {
          setFetchingUserData(false);
          setFetchUserDataComplete(true);
        }
      };

      fetchUserDataAsync();
    }
  }, [graphApi, userApi, accounts, instance, fetchingUserData, fetchUserDataComplete]);

  useEffect(() => {
    // Require a resolved Graph profile so we can build a stable cache key
    if (!me) return;

    if (!fetchingProfileImage && !myAAPhotoUrl && !fetchProfileImageComplete) {
      const backendUserId = me.objectId || me.id || 'default';
      const photoCacheKey = `aad_profile_photo_${backendUserId}`;

      setFetchingProfileImage(true);
      graphApi.fetchProfilePhoto()
        .then(data => {
          setMyAAPhotoUrl(data);
          try {
            CacheService.set(photoCacheKey, data);
          } catch (_e) { /* non-fatal if photo cache fails */ }
        })
        .catch(err => {
          console.error('Failed to load image', err);
          try {
            const cached = CacheService.get(photoCacheKey, Number.MAX_SAFE_INTEGER);
            if (cached) {
              setMyAAPhotoUrl(cached);
            }
          } catch (_e) { /* ignore cache fallback errors */ }
        })
        .finally(() => { setFetchingProfileImage(false); setFetchProfileImageComplete(true); });
    }
  }, [fetchingProfileImage, myAAPhotoUrl, fetchProfileImageComplete, graphApi, me]);

  return <UserProfileContext.Provider value={{ graphMe: me, systemProfile: myProfile, aadProfileImage: myAAPhotoUrl }}>{children}</UserProfileContext.Provider>;
};

export const SignalRProvider = ({ children }) => {
  const { instance, inProgress, accounts } = useMsal();
  const signalR = useMemo(() => new SignalRApi(instance, inProgress, accounts, false), [instance, inProgress, accounts]);
  useEffect(() => { if (signalR && signalR.state !== 'Started' && signalR.state !== 'Starting') { signalR.start().catch(err => console.log('Error connecting to SignalR', err)); } }, [signalR]);
  return <SignalRContext.Provider value={signalR}>{children}</SignalRContext.Provider>;
};

export const MenuProvider = ({ children }) => {
  const [menus, setMenus] = useState(null);
  const [shouldGetMenus, setShouldGetMenus] = useState(true);
  const [fetchComplete, setFetchComplete] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const [expandedMenu, setExpandedMenu] = useState(null);
  const { accounts } = useMsal();
  
  const userProfile = useContext(UserProfileContext);
  const api = useMemo(() => new MenuAPI(), []);
  
  useEffect(() => {
    if (!fetchComplete && shouldGetMenus) {
      // Prefer backend user profile roles if available
      const apiRoles = userProfile?.systemProfile?.apiRoles || [];
      let roles = arrayAny(apiRoles)
        ? apiRoles.map(mapApiRoleToMenuRole)
        : (accounts && accounts[0]?.idTokenClaims?.roles) || [];

      if (!arrayAny(roles)) {
        // No roles yet – wait for profile/accounts to load
        return;
      }

      api.getMenuItems(roles)
        .then(data => setMenus(data))
        .catch(error => console.log('An error occurred loading menu data', error))
        .finally(() => { setShouldGetMenus(false); setFetchComplete(true); });
    }
  }, [shouldGetMenus, menus, api, fetchComplete, accounts, userProfile]);
  useEffect(() => { const handleMenuUpdate = e => setMenus([...e.detail]); window.addEventListener('menuUpdate', handleMenuUpdate); return () => window.removeEventListener('menuUpdate', handleMenuUpdate); }, []);
  return <MenuContext.Provider value={{ menus, activeMenu, setActiveMenu, expandedMenu, setExpandedMenu }}>{children}</MenuContext.Provider>;
};

export const useUserProfile = () => useContext(UserProfileContext);
export const useSignalR = () => useContext(SignalRContext);
export const useMenus = () => { const context = useContext(MenuContext); return context?.menus || context; };
export const useMenuContext = () => useContext(MenuContext);
